import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ClipboardCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  getUnverifiedHumanVerifyItems,
  type HumanVerifyItem,
  type HumanVerifyState,
} from '@/lib/humanVerify';
import {
  HumanVerifyItemRow,
  type HumanVerifyResolution,
} from './HumanVerifyItemRow';

export interface HumanVerifyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  humanVerify: HumanVerifyState | null;
  initialItemId?: string | null;
  resolvingItemId?: string | null;
  onResolve: (item: HumanVerifyItem, status: HumanVerifyResolution, note: string) => void;
}

export function HumanVerifyModal({
  open,
  onOpenChange,
  humanVerify,
  initialItemId,
  resolvingItemId,
  onResolve,
}: HumanVerifyModalProps) {
  const items = humanVerify?.items ?? [];
  const unverified = useMemo(
    () => getUnverifiedHumanVerifyItems(humanVerify),
    [humanVerify],
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const requested = initialItemId
      ? items.find((item) => item.id === initialItemId)
      : null;
    setActiveId(requested?.id ?? unverified[0]?.id ?? items[0]?.id ?? null);
  }, [initialItemId, items, open, unverified]);

  const activeIndex = activeId
    ? items.findIndex((item) => item.id === activeId)
    : -1;
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;
  const canGoPrevious = activeIndex > 0;
  const canGoNext = activeIndex >= 0 && activeIndex < items.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-hidden p-0">
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="size-4 text-coral" />
            Review flagged items
          </DialogTitle>
          <DialogDescription>
            Review each flagged item and record the decision.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[360px] overflow-y-auto px-5 py-4">
          {activeItem ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs font-semibold text-muted-foreground">
                  Item {activeIndex + 1} of {items.length}
                </div>
                <div
                  className={cn(
                    'rounded-md border px-2 py-0.5 text-xs font-semibold',
                    unverified.length > 0
                      ? 'border-coral/30 bg-coral-light text-coral-dark'
                      : 'border-teal/30 bg-teal-light text-teal-dark',
                  )}
                >
                  {unverified.length} unverified
                </div>
              </div>
              <HumanVerifyItemRow
                item={activeItem}
                onResolve={onResolve}
                resolving={resolvingItemId === activeItem.id}
              />
            </div>
          ) : (
            <div className="flex min-h-[320px] items-center justify-center rounded-md border border-dashed border-border bg-warm-bg px-4 text-center text-sm text-muted-foreground">
              No flagged reviewer checks for this article.
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-border bg-warm-bg px-5 py-3">
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => canGoPrevious && setActiveId(items[activeIndex - 1].id)}
                disabled={!canGoPrevious}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2.5 text-xs font-semibold text-foreground hover:bg-card disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ChevronLeft className="size-3.5" />
                Previous
              </button>
              <button
                type="button"
                onClick={() => canGoNext && setActiveId(items[activeIndex + 1].id)}
                disabled={!canGoNext}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2.5 text-xs font-semibold text-foreground hover:bg-card disabled:cursor-not-allowed disabled:opacity-60"
              >
                Next
                <ChevronRight className="size-3.5" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-8 items-center rounded-md bg-coral px-3 text-xs font-semibold text-primary-foreground hover:bg-coral-hover"
            >
              Done
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
